import {
  Edit,
  SimpleForm,
  TextInput,
  SelectInput,
  DateField,
  Toolbar,
  SaveButton,
  DeleteButton,
  usePermissions,
  useRecordContext,
} from 'react-admin';
import { Box, Typography, Card, CardContent, Grid, Chip, Alert } from '@mui/material';

const roleChoices = [
  { id: 'author', name: 'Author' },
  { id: 'editor', name: 'Editor' },
  { id: 'admin', name: 'Admin' },
  { id: 'super_admin', name: 'Super Admin' },
];

const ProviderChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  const colorMap: Record<string, 'primary' | 'secondary'> = {
    google: 'primary',
    github: 'secondary',
  };

  return (
    <Chip
      label={record.oauth_provider}
      color={colorMap[record.oauth_provider] || 'default'}
      size="small"
      sx={{ textTransform: 'capitalize' }}
    />
  );
};

const EditToolbar = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();
  const canDelete = permissions?.role === 'super_admin';
  const isSelf = record?.id === permissions?.userId;

  return (
    <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <SaveButton />
      {canDelete && !isSelf && <DeleteButton />}
    </Toolbar>
  );
};

const CurrentUserWarning = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();

  if (!record || record.id !== permissions?.userId) return null;

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      You are editing your own account. Some changes may require you to log out and back in.
    </Alert>
  );
};

export const AdminUserEdit = () => {
  const { permissions } = usePermissions();
  const canManageUsers =
    permissions?.role === 'super_admin' || permissions?.role === 'admin';
  const isSuperAdmin = permissions?.role === 'super_admin';

  if (!canManageUsers) {
    return null;
  }

  return (
    <Edit>
      <SimpleForm toolbar={<EditToolbar />}>
        <CurrentUserWarning />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  User Information
                </Typography>

                <TextInput source="display_name" fullWidth label="Display Name" />

                <TextInput source="email" fullWidth disabled label="Email" />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    OAuth Provider
                  </Typography>
                  <ProviderChip />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Role & Permissions
                </Typography>

                <SelectInput
                  source="role"
                  choices={roleChoices}
                  fullWidth
                  disabled={!isSuperAdmin}
                  helperText={
                    !isSuperAdmin ? 'Only super admins can change roles' : undefined
                  }
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Last Login
                  </Typography>
                  <DateField source="last_login_at" showTime />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    Created At
                  </Typography>
                  <DateField source="created_at" showTime />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </SimpleForm>
    </Edit>
  );
};
